// src/utils/loadFees.js
//
// A load can carry a broker fee AND a factoring fee at the same time. Both are
// deducted; they are separate costs and a load may incur either, both, or none.
//
// History matters here. Before the two were separated there was ONE input, and
// a checkbox relabelled it: a factored load stored its factoring fee in
// `brokerFeeCollection` with `factoringApplied: true`. Reading those straight
// would relabel years of factoring fees as broker fees and change what every
// historical statement says.
//
// So old loads are reinterpreted on read rather than rewritten. Nothing is
// migrated, nothing is lost, and both old and new loads report the truth.

export const BROKER_FEE_FIELD = 'brokerFeeCollection';
export const FACTORING_FEE_FIELD = 'factoringFee';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @returns {{brokerFee: number, factoringFee: number, total: number, isLegacy: boolean}}
 */
export function getLoadFees(load) {
  const stored = num(load?.[BROKER_FEE_FIELD]);
  const factoring = num(load?.[FACTORING_FEE_FIELD]);

  // A pre-split factored load: factoringApplied set, and no separate factoring
  // fee ever written. Its single stored fee was the factoring fee.
  const isLegacy = Boolean(load?.factoringApplied) && load?.[FACTORING_FEE_FIELD] == null;

  if (isLegacy) {
    return { brokerFee: 0, factoringFee: stored, total: stored, isLegacy: true };
  }

  return {
    brokerFee: stored,
    factoringFee: factoring,
    total: stored + factoring,
    isLegacy: false,
  };
}

/** Everything deducted from the load price before driver pay. */
export function totalFees(load) {
  return getLoadFees(load).total;
}

/** "$150 broker + $75 factoring", or a single part, or null when there are none. */
export function describeFees(load) {
  const { brokerFee, factoringFee } = getLoadFees(load);
  const parts = [];
  if (brokerFee) parts.push(`$${brokerFee.toFixed(2)} broker`);
  if (factoringFee) parts.push(`$${factoringFee.toFixed(2)} factoring`);
  return parts.length ? parts.join(' + ') : null;
}
